import { di } from 'react-magnetic-di';

import { fetchDraftBuild } from './fetch-draft-build';
import { updateBuildState } from './update-build-state';

declare global {
	interface Window {
		__env__?: string;
	}
}

type DraftContent = {
	rule?: Record<string, unknown>;
	connections?: unknown[];
	specification?: string;
};

function getDraftRulePayload(draftContent: DraftContent): {
	ruleConfig?: Record<string, unknown>;
	newConnections?: unknown[];
} {
	if (draftContent.rule) {
		return { ruleConfig: draftContent.rule, newConnections: draftContent.connections };
	}

	if (draftContent.specification) {
		const specification = JSON.parse(draftContent.specification) as DraftContent;
		return { ruleConfig: specification.rule, newConnections: specification.connections };
	}

	return {};
}

function buildStudioSc3RulesBasePath(workspaceId: string, spaceId: string): string {
	const appEnv = typeof process !== 'undefined' ? process.env.APP_ENV : undefined;
	const windowEnv = typeof window !== 'undefined' ? window.__env__ : undefined;
	const env =
		appEnv === 'prod' || appEnv === 'prod-east' || windowEnv === 'prod' || windowEnv === 'prod-east'
			? 'pro'
			: 'staging';

	return `/gateway/api/automation/sc3/o/studio/t/workspace/i/${workspaceId}/internal-api/${env}/rest/${spaceId}/rule`;
}

export async function saveAutomation({
	buildId,
	workspaceId,
	spaceId,
	publish,
}: {
	buildId: string;
	workspaceId: string;
	spaceId: string;
	publish: boolean;
}): Promise<void> {
	di(fetch, fetchDraftBuild, updateBuildState);

	const draftBuild = await fetchDraftBuild(buildId, workspaceId);
	const draftContent = JSON.parse(draftBuild.draftContent) as DraftContent;
	const { ruleConfig, newConnections } = getDraftRulePayload(draftContent);

	if (!ruleConfig) {
		throw new Error('Invalid draft content: missing rule.');
	}

	const existingTags = Array.isArray(ruleConfig.tags) ? ruleConfig.tags : [];
	const body = {
		ruleConfigBean: {
			...ruleConfig,
			state: publish ? 'ENABLED' : 'DISABLED',
			isNewRule: true,
			tags: [...existingTags, { tagType: 'SOURCE', tagValue: 'studio-sa-create' }],
		},
		newConnections,
	};

	const response = await fetch(buildStudioSc3RulesBasePath(workspaceId, spaceId), {
		method: 'POST',
		headers: { 'Content-Type': 'application/json', Accept: '*/*' },
		credentials: 'include',
		body: JSON.stringify(body),
	});

	if (!response.ok) {
		throw new Error('Failed to create automation rule.');
	}

	const text = await response.text();
	const created = text ? JSON.parse(text) : {};
	const ruleAri = created.idUuid ? `ari:cloud:automation::rule/${created.idUuid}` : undefined;

	try {
		await updateBuildState(workspaceId, {
			buildId,
			state: 'SUCCESS',
			objectAri: ruleAri,
		});
	} catch {
		// The automation rule was created successfully. Do not reject here, otherwise retrying
		// would create a duplicate rule; the user can still see the saved state locally.
	}
}
